import { MembershipActionBlockedReason, MembershipCapabilities, UserMembershipView } from '@localstack/types';

export type MembershipAction = keyof MembershipCapabilities;

export type MembershipActionAvailability = {
  allowed: boolean;
  reason: string | null;
};

export type MembershipActionsAvailability = Record<MembershipAction | 'menu', MembershipActionAvailability>;

export type LocalMembershipRules = {
  canUpdateMembers: boolean;
  canDeleteMembers: boolean;
  isLastAdmin: boolean;
  scimManaged: boolean;
};

const BLOCKED_REASON_MESSAGES: Record<MembershipAction | 'menu', Record<MembershipActionBlockedReason, string>> = {
  menu: {
    [MembershipActionBlockedReason.SELF]: 'You cannot manage your own membership',
    [MembershipActionBlockedReason.MISSING_PERMISSION]: 'You do not have permission to manage members',
    [MembershipActionBlockedReason.ROLE_HIERARCHY]: 'You cannot manage a member with a higher role than yours',
    [MembershipActionBlockedReason.SCIM_MANAGED]: 'Membership is managed via SCIM',
  },
  change_role: {
    [MembershipActionBlockedReason.SELF]: 'You cannot change your own role',
    [MembershipActionBlockedReason.MISSING_PERMISSION]: 'You do not have permission to change member roles',
    [MembershipActionBlockedReason.ROLE_HIERARCHY]:
      'You cannot change the role of a member with a higher role than yours',
    [MembershipActionBlockedReason.SCIM_MANAGED]: 'Role is managed via SCIM',
  },
  edit_permissions: {
    [MembershipActionBlockedReason.SELF]: 'You cannot change your own permissions',
    [MembershipActionBlockedReason.MISSING_PERMISSION]: 'You do not have permission to change member permissions',
    [MembershipActionBlockedReason.ROLE_HIERARCHY]:
      'You cannot change the permissions of a member with a higher role than yours',
    [MembershipActionBlockedReason.SCIM_MANAGED]: 'Permissions are managed via SCIM',
  },
  remove: {
    [MembershipActionBlockedReason.SELF]: 'You cannot remove yourself from this workspace',
    [MembershipActionBlockedReason.MISSING_PERMISSION]: 'You do not have permission to remove members',
    [MembershipActionBlockedReason.ROLE_HIERARCHY]: 'You cannot remove a member with a higher role than yours',
    [MembershipActionBlockedReason.SCIM_MANAGED]: 'Removal is managed via SCIM',
  },
};

const availability = (allowed: boolean, reason: string | null = null): MembershipActionAvailability => ({
  allowed,
  reason: allowed ? null : reason,
});

const describeBlockedReason = (
  action: MembershipAction | 'menu',
  blockedReason: MembershipActionBlockedReason | undefined,
): string | null => (blockedReason ? BLOCKED_REASON_MESSAGES[action][blockedReason] : null);

const MENU_REASON_PRIORITY: MembershipActionBlockedReason[] = [
  MembershipActionBlockedReason.SELF,
  MembershipActionBlockedReason.ROLE_HIERARCHY,
  MembershipActionBlockedReason.SCIM_MANAGED,
  MembershipActionBlockedReason.MISSING_PERMISSION,
];

const mostSpecificBlockedReason = (capabilities: MembershipCapabilities): MembershipActionBlockedReason | undefined => {
  const blockedReasons = Object.values(capabilities).map((capability) => capability.blocked_reason);
  return MENU_REASON_PRIORITY.find((reason) => blockedReasons.includes(reason));
};

const fromCapabilities = (capabilities: MembershipCapabilities): MembershipActionsAvailability => {
  const availabilityOf = (action: MembershipAction) =>
    availability(capabilities[action].allowed, describeBlockedReason(action, capabilities[action].blocked_reason));

  const changeRole = availabilityOf('change_role');
  const editPermissions = availabilityOf('edit_permissions');
  const remove = availabilityOf('remove');

  return {
    menu: availability(
      changeRole.allowed || editPermissions.allowed || remove.allowed,
      describeBlockedReason('menu', mostSpecificBlockedReason(capabilities)),
    ),
    change_role: changeRole,
    edit_permissions: editPermissions,
    remove,
  };
};

const fromLocalRules = ({
  canUpdateMembers,
  canDeleteMembers,
  isLastAdmin,
  scimManaged,
}: LocalMembershipRules): MembershipActionsAvailability => {
  const scimBlockedReason = scimManaged ? MembershipActionBlockedReason.SCIM_MANAGED : undefined;

  return {
    menu: availability(canUpdateMembers && canDeleteMembers),
    change_role: availability(!isLastAdmin && !scimManaged, describeBlockedReason('change_role', scimBlockedReason)),
    edit_permissions: availability(true),
    remove: availability(!isLastAdmin && !scimManaged, describeBlockedReason('remove', scimBlockedReason)),
  };
};

export const resolveMembershipActions = (
  member: UserMembershipView,
  localRules: LocalMembershipRules,
): MembershipActionsAvailability =>
  member.capabilities ? fromCapabilities(member.capabilities) : fromLocalRules(localRules);
